import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useUserPkgs } from '@/api/user-package/queries/useUserPkgs'
import { PkgStatus, UserPkg } from '@/api/user-package/types/UserPkg'
import { AppTooltip } from '@/components/AppTooltip'
import UniteIcon from '@/components/icons/pkg-unite.svg?react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import {
  canAddToExistingUnite,
  getIsPkgReqActive,
  getPendingUniteGroups,
  getPkgContentsItemCount,
  getPkgStatus,
  getPkgStatusTooltip,
  getShippingMethodLabel,
  userPkgToUniteSummary,
} from '@/helpers/pkgUtils'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/RoutePath'

import { BrandPreview } from '../../../../components/BrandPreview'
import { Divider } from '../../../../components/Divider'
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover'
import { AddToExistingUniteDialog } from '../../UnitePkg/cmps/AddToExistingUniteDialog'
import ClientPkgDetails from './ClientPkgDetails'
import { DeliveryStatus } from './DeliveryStatus'
import { PkgReqMenu } from './PkgReqButtons'

interface Props {
  pkg: UserPkg
  currentView: 'all' | 'shipped' | 'identify'
  hideActions?: boolean
  hideStatus?: boolean
  isSelected?: boolean
  additionalStatus?: string
  /** Force the more-actions menu open (used by onboarding demo). */
  forceActionsOpen?: boolean
  onSelect?: (pkgId: string) => void
  onPkgPayment?: (pkg: UserPkg) => void
  onPkgIdentify?: (pkgId: string) => void
}
export const PkgPreview = ({
  pkg,
  currentView,
  hideActions,
  isSelected,
  additionalStatus,
  hideStatus,
  forceActionsOpen,
  onSelect,
  onPkgPayment,
  onPkgIdentify,
}: Props) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { data: userPkgs } = useUserPkgs()
  const [isReqMenuOpen, setIsReqMenuOpen] = useState(Boolean(forceActionsOpen))
  const [isDeliveryStatusOpen, setIsDeliveryStatusOpen] = useState(false)
  const [isAddToUniteOpen, setIsAddToUniteOpen] = useState(false)
  const showAddToUnite = !hideActions && canAddToExistingUnite(pkg, userPkgs)
  // Tour forces the menu open on the same render (no 300ms expand animation) so the spotlight measures the final size.
  const showReqMenu = Boolean(forceActionsOpen) || isReqMenuOpen

  useEffect(() => {
    if (forceActionsOpen) {
      setIsReqMenuOpen(true)
    }
  }, [forceActionsOpen])

  const addToUniteButton = showAddToUnite ? (
    <Button
      variant='outline'
      size='sm'
      className='gap-2 rounded-full'
      onClick={e => {
        e.stopPropagation()
        setIsAddToUniteOpen(true)
      }}
    >
      <UniteIcon className='text-icon-primary size-5' />
      <span className='font-normal'>{t('unite.addToExistingButton')}</span>
    </Button>
  ) : null

  const mainButton = useMemo(() => {
    // Show delivery status button for shipped packages (works in all views)
    if (pkg.isShipped) {
      return (
        <Button className='max-w-[126px]' onClick={() => setIsDeliveryStatusOpen(true)}>
          {t('pkg.deliveryStatus')}
        </Button>
      )
    }

    // Don't show button if in non-all views
    if (currentView !== 'all') return null

    // Handle active requests (photo, shrink, unite)
    if (getIsPkgReqActive(pkg)) {
      return null
      // <AppTooltip triggerProps={{ asChild: true }} content='לא ניתן לשלח את החבילה כשיש בקשה בטיפול'>
      //   <Button disabled className='disabled:pointer-events-auto disabled:cursor-default disabled:hover:shadow-none'>
      //     בקשה ממתינה לביצוע
      //   </Button>
      // </AppTooltip>
    }
    if (pkg.isPaid && !pkg.isShipped) {
      return null
    }

    // Handle return requests
    if (pkg.isReturnRequestActive) {
      if (pkg.returnLabelFalse) {
        return (
          <Link to={`${RoutePath.ClientPkgReturn}/${pkg.id}`}>
            <Button className='max-w-[126px]'>{t('pkg.uploadReturnLabel')}</Button>
          </Link>
        )
      }

      return null
    }

    // Handle identification needed
    if (pkg.status === PkgStatus.NeedId && onPkgIdentify) {
      return (
        <Button className='max-w-[126px]' onClick={() => onPkgIdentify(pkg.id)}>
          {t('pkg.continueToIdentify')}
        </Button>
      )
    }

    // Default: continue to shipping
    if (onPkgPayment) {
      return (
        <Button className='max-w-[126px]' onClick={() => onPkgPayment(pkg)}>
          {t('pkg.continueToShip')}
        </Button>
      )
    }

    return null
  }, [pkg, currentView, onPkgIdentify, onPkgPayment, t])

  const statusCmp = useMemo(() => {
    if (hideStatus) return null
    const tooltipContent = getPkgStatusTooltip(pkg, t)
    const status = getPkgStatus(pkg, t)
    if (!status) return null
    const content = (
      <div className='text-text-secondary bg-button-primary-selected-segment flex items-center gap-2 rounded-full px-3 py-1 text-center text-xs'>
        {status}
      </div>
    )
    if (tooltipContent) {
      return <AppTooltip content={tooltipContent}>{content}</AppTooltip>
    }
    return content
  }, [pkg, hideStatus, t])
  const onPkgClick = () => {
    if (onSelect && isMobile) {
      onSelect(pkg.id)
    }
  }
  return (
    <div
      key={pkg.id}
      className={cn(
        'border-border-default flex flex-col gap-4 overflow-hidden rounded-2xl border bg-white p-4 shadow-sm',
        isMobile && isSelected && 'border-button-secondary-border-default shadow-[0px_0px_0px_2px_rgba(0,174,255,0.2)]'
      )}
      onClick={onPkgClick}
    >
      <div className='flex gap-6'>
        {!isMobile && onSelect && <Checkbox checked={isSelected} onCheckedChange={() => onSelect?.(pkg.id)} />}
        <div className='grow space-y-4'>
          {/* Package Header */}
          <div className='flex items-start justify-between'>
            <BrandPreview brand={pkg.brand} warehouse={pkg.warehouse} />
            <div className='flex flex-wrap items-center justify-end gap-3'>
              {additionalStatus && (
                <div className='bg-button-primary-selected-segment text-text-secondary rounded-full px-3 py-1 text-center text-xs'>
                  {additionalStatus}
                </div>
              )}
              {statusCmp}
            </div>
          </div>

          {/* Package Details */}
          <div className='flex flex-wrap items-center gap-8 text-sm'>
            <div>
              <p className='text-text-secondary text-xs'>{t('pkg.arrivalDate')}</p>
              <p className='font-medium'>{pkg.arrivalDate.split('-').reverse().join('.')}</p>
            </div>
            <div className='hidden md:block'>
              <p className='text-text-secondary text-xs'>{t('pkg.weight')}</p>
              <p className='font-medium'>{pkg.dimensions.weight}KG</p>
            </div>
            <div>
              <p className='text-text-secondary text-xs'>{t('pkg.quantity')}</p>
              <p className='font-medium'>
                {getPkgContentsItemCount(pkg.contents)} {t('pkg.items')}
              </p>
            </div>
            {pkg.valueInUSD > 0 && (
              <div>
                <p className='text-text-secondary text-xs'>{t('pkg.packageValue')}</p>
                <p className='font-medium'>{pkg.valueInUSD}$</p>
              </div>
            )}
            {pkg.isShipped && (
              <>
                <div>
                  <p className='text-text-secondary text-xs'>{t('pkg.trackingNumber')}</p>
                  <p className='font-medium'>{pkg.toTrackingNumber}</p>
                </div>
                <div>
                  <p className='text-text-secondary text-xs'>{t('pkg.shippingMethodLabel')}</p>
                  <p className='font-medium'>{getShippingMethodLabel(pkg.shippingMethod, t)}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <Divider />

      {/* Actions */}
      <div>
        {pkg.status === 'needs_identification' && (
          <div className='space-y-3'>
            <div className='flex items-center justify-center gap-4'>
              <Button
                variant='outline'
                size='sm'
                // onClick={() => handlePackageIdentification(pkg.id, false)}
                // className={packageIdentifications[pkg.id] === false ? 'bg-button-primary-selected-segment' : ''}
              >
                {t('pkg.no')}
              </Button>
              <Button
                variant='outline'
                size='sm'
                // onClick={() => handlePackageIdentification(pkg.id, true)}
                // className={packageIdentifications[pkg.id] === true ? 'bg-button-primary-selected-segment' : ''}
              >
                {t('pkg.yes')}
              </Button>
            </div>
            <p className='text-text-secondary text-center text-sm'>{t('pkg.isThisYourPackage')}</p>
          </div>
        )}

        {isMobile ? (
          <div className='flex flex-col gap-2 transition-all duration-300 ease-in-out' data-tour='tour-pkg-actions'>
            {!hideActions && (
              <div className='flex w-full flex-col items-center justify-center gap-2'>
                {mainButton}
                {addToUniteButton}
              </div>
            )}
            <div className='flex items-center justify-between'>
              <Drawer>
                <DrawerTrigger asChild>
                  <Button variant='link' size='sm' className='text-text-link font-medium hover:no-underline'>
                    {t('pkg.moreDetails')}
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <ClientPkgDetails pkg={pkg} />
                </DrawerContent>
              </Drawer>
              {!hideActions && !pkg.isShipped && (pkg.canCancelReturnRequest || pkg.canCancelShipment || !pkg.isPaid) && (
                <Button variant='ghost' size='sm' className='gap-1' onClick={() => setIsReqMenuOpen(p => !p)}>
                  {showReqMenu ? (
                    <ChevronUp className='text-text-secondary size-4' />
                  ) : (
                    <ChevronDown className='text-text-secondary size-4' />
                  )}
                  <span className='text-text-secondary text-xs'>{t('pkg.moreActions')}</span>
                </Button>
              )}
            </div>
            <div
              className={cn('h-0 overflow-hidden overflow-x-auto opacity-0', {
                'transition-all duration-300 ease-in-out': !forceActionsOpen,
                'h-12 opacity-100': showReqMenu,
              })}
            >
              <PkgReqMenu pkg={pkg} className='justify-between' />
            </div>
          </div>
        ) : (
          <div className='flex items-center justify-between' data-tour='tour-pkg-actions'>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant='link' size='sm' className='text-text-link font-medium hover:no-underline'>
                  {t('pkg.moreDetails')}
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <ClientPkgDetails pkg={pkg} />
              </PopoverContent>
            </Popover>
            {!hideActions && (
              <>
                <div className='flex items-center gap-2'>
                  {!pkg.isShipped && (pkg.canCancelReturnRequest || pkg.canCancelShipment || !pkg.isPaid) && (
                    <>
                      <div
                        className={cn(
                          'transform',
                          forceActionsOpen ? undefined : 'transition-all duration-300 ease-in-out',
                          showReqMenu ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-2 opacity-0'
                        )}
                      >
                        <PkgReqMenu pkg={pkg} />
                      </div>
                      <Button variant='ghost' size='sm' className='gap-1' onClick={() => setIsReqMenuOpen(p => !p)}>
                        {showReqMenu ? (
                          <ChevronLeft className='text-text-secondary size-4' />
                        ) : (
                          <ChevronRight className='text-text-secondary size-4' />
                        )}
                        <span className='text-text-secondary text-xs'>{t('pkg.moreActions')}</span>
                      </Button>
                    </>
                  )}
                  {addToUniteButton}
                  {mainButton}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Delivery Status Dialog */}
      <DeliveryStatus toTrackingNumber={pkg.toTrackingNumber} isOpen={isDeliveryStatusOpen} onOpenChange={setIsDeliveryStatusOpen} />
      {showAddToUnite && userPkgs && (
        <AddToExistingUniteDialog
          isOpen={isAddToUniteOpen}
          onClose={() => setIsAddToUniteOpen(false)}
          pkg={userPkgToUniteSummary(pkg)}
          uniteGroups={getPendingUniteGroups(userPkgs, pkg.warehouse)}
          successMode='client'
        />
      )}
    </div>
  )
}
